import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import type { ReportCampaignOption } from '../types/reports';
import { getCampaignOptionId, getCampaignOptionName } from '../utils/reportsUtils';

type CampaignMultiSelectProps = {
  options: ReportCampaignOption[];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  error?: string;
};

export function CampaignMultiSelect({
  options,
  value,
  onChange,
  disabled,
  error,
}: CampaignMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const filtered = options.filter((opt) => {
    const name = getCampaignOptionName(opt).toLowerCase();
    const id = getCampaignOptionId(opt).toLowerCase();
    const q = query.toLowerCase();
    return name.includes(q) || id.includes(q);
  });

  const label =
    value.length === 0
      ? 'Find campaigns by name or ID…'
      : value.length === options.length
        ? 'All campaigns selected'
        : `${value.length} campaign${value.length === 1 ? '' : 's'} selected`;

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div ref={rootRef} className="relative flex w-full flex-col gap-1.5 text-left">
      <label className="text-[13px] font-semibold text-[var(--dash-ink)]">
        Select Specific Campaigns
      </label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-[var(--dash-soft)] px-3.5 py-3 text-left text-sm outline-none transition ${
          error ? 'border-red-400' : 'border-[var(--dash-line)]'
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:border-[var(--dash-brand)]'} ${
          open ? 'border-[var(--dash-brand)] bg-white ring-2 ring-[var(--dash-brand)]/15' : ''
        }`}
      >
        <span className={value.length ? 'text-[var(--dash-ink)]' : 'text-[#9AA8B8]'}>
          {label}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[var(--dash-muted)] transition ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div className="absolute top-[calc(100%+6px)] z-30 max-h-64 w-full overflow-hidden rounded-xl border border-[var(--dash-line)] bg-white shadow-[0_18px_40px_-20px_rgba(16,38,73,0.45)]">
          <div className="border-b border-[var(--dash-line)] bg-[var(--dash-soft)]/50 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-[var(--dash-muted)]" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or ID…"
                className="w-full rounded-lg border border-[var(--dash-line)] bg-white py-2 pr-3 pl-8 text-sm outline-none focus:border-[var(--dash-brand)]"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-[var(--dash-muted)] italic">
                {query ? 'No campaigns found' : 'No campaigns available'}
              </p>
            ) : (
              filtered.map((opt) => {
                const id = getCampaignOptionId(opt);
                const selected = value.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggle(id)}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition ${
                      selected
                        ? 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]'
                        : 'text-[var(--dash-ink)] hover:bg-[var(--dash-soft)]'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? 'border-[var(--dash-brand)] bg-[var(--dash-brand)]'
                          : 'border-[var(--dash-line)] bg-white'
                      }`}
                    >
                      {selected ? <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} /> : null}
                    </span>
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate font-medium">{getCampaignOptionName(opt)}</span>
                      <span className="text-[11px] text-[var(--dash-muted)]">ID: {id}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}

      {error ? <p className="m-0 mt-0.5 text-xs font-medium text-red-500">{error}</p> : null}
    </div>
  );
}
